import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { capitalizeWords, getSession } from "../../functions";
import { fetchGatePassDetails } from "../../redux/gatePassSlice";
import Select from "react-select";

function GatePass() {
  const dispatch = useDispatch();
  const { data: gatePassDetails, loading: fetchLoading } = useSelector(
    (state) => state.gatePass,
  );

  useEffect(() => {
    dispatch(fetchGatePassDetails());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];
  const [tempAdmission, setTempAdmission] = useState("");
  const session = getSession();

  const [formData, setFormData] = useState({
    admission_no: [],
    staffId: "",
    outTime: "",
    purpose: "",
    remark: "",
    status: "pending",
    type: "student",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (selectedOptions, fieldName) => {
    const values = selectedOptions.map((option) => option.value);
    console.log("Selected Admission Nos:", values);
    setFormData((prev) => ({ ...prev, [fieldName]: values }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    if (formData.type === "student" && formData.admission_no.length === 0) {
      toast.error("Please enter at least one Admission No");
      setLoading(false);
      return;
    }
    if (formData.type === "staff" && !formData.staffId) {
      toast.error("Please enter Staff ID");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        type: formData.type,
        outTime: formData.outTime,
        purpose: formData.purpose,
        remark: formData.remark,
        status: formData.status,
        ...(formData.type === "student"
          ? { admission_no: formData.admission_no }
          : { staffId: formData.staffId, admission_no: [] }),
      };

      const response = await axios.post(
        `${server}/create_gate_pass_mongodb`,
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Gate Pass Created", { position: "top-right" });
      dispatch(fetchGatePassDetails());

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      setFormData({
        admission_no: [],
        staffId: "",
        outTime: "",
        purpose: "",
        remark: "",
        status: "pending",
        type: "student",
      });
      setTempAdmission("");
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const flattenObjectValues = (obj) => {
    let values = [];
    for (let key in obj) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        values = values.concat(flattenObjectValues(value));
      } else if (typeof value === "string") {
        values.push(value);
      }
    }
    return values;
  };

  const filteredRecords = gatePassDetails.filter((gateDetails) => {
    const searchLower = searchTerm.toLowerCase();
    const allValues = flattenObjectValues(gateDetails);
    return allValues.some((val) => val.toLowerCase().includes(searchLower));
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const fetchStudentDetails = async (admissionNo) => {
    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const studentData = response.data?.["body-json"]?.body?.student || null;

      if (!studentData) {
        console.warn(`No student found for admission number: ${admissionNo}`);
      }

      return studentData;
    } catch (error) {
      console.error(
        `Error fetching student details for ${admissionNo}:`,
        error,
      );
      return null;
    }
  };

  const fetchStaffDetails = async (staffId) => {
    try {
      const response = await axios.post(
        `${server}/get_staff_data_mongodb`,
        { staffId },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data?.["body-json"]?.body || null;
    } catch (error) {
      console.error(`Error fetching staff details for ${staffId}:`, error);
      return null;
    }
  };

  const handleDownloadPDF = async (gatePass) => {
    const doc = new jsPDF("p", "mm", "a4");

    let xOffset = 10;
    let yOffset = 25;
    let passCount = 0;
    let rowStartY = yOffset;

    // ── STAFF GATE PASS ──────────────────────────────────────────
    if (gatePass.type === "staff") {
      const staff = await fetchStaffDetails(gatePass.staffId);

      if (!staff) {
        toast.error("Staff data not found");
        return;
      }

      doc.setFillColor(240, 240, 240);
      doc.roundedRect(xOffset - 2, rowStartY - 2, 94, 65, 5, 5, "F");

      const schoolLogo = "/assets/images/icon.png";
      doc.addImage(schoolLogo, "PNG", xOffset, rowStartY, 13, 13);

      doc.setFont("helvetica", "bold").setFontSize(10.5);
      doc.text("Acharyakulam", xOffset + 16, rowStartY + 4);

      doc.setFont("helvetica", "normal").setFontSize(7);
      doc.text(
        "Tata Road, Namkum, Ranchi - 834010",
        xOffset + 16,
        rowStartY + 6.8,
      );
      doc.text(`Session ${session}`, xOffset + 16, rowStartY + 9);

      doc.setFont("helvetica", "bold").setFontSize(8);
      doc.setTextColor(239, 78, 36);
      doc.text("Teacher's Gate Pass", xOffset + 16, rowStartY + 12); // ✅ Changed
      doc.setTextColor(0, 0, 0);

      const tableStartX = xOffset;
      const tableStartY = rowStartY + 15;
      const tableWidth = 90;
      const tableHeight = 98;
      const cornerRadius = 5;
      const topPadding = 5;

      doc.setDrawColor(239, 78, 36);
      doc.setLineWidth(0.5);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(
        tableStartX,
        tableStartY,
        tableWidth,
        tableHeight,
        cornerRadius,
        cornerRadius,
        "DF",
      );

      autoTable(doc, {
        startY: tableStartY + topPadding,
        margin: { left: xOffset },
        tableWidth: tableWidth,
        body: [
          ["Gate Pass ID", gatePass.gatePassId || "N/A"],
          ["Name", staff.basicDetails?.fullName || "N/A"],
          ["Staff ID", staff.staffId || "N/A"], // ✅ Admission No → Staff ID
          ["Designation", staff.categoryDetails?.designation || "N/A"],
          ["Department", staff.categoryDetails?.department || "N/A"],
          ["Purpose", gatePass.purpose || "N/A"],
          ["Remark", gatePass.remark || "N/A"],
          ["Out Time", gatePass.outTime || "N/A"],
          ["Date", new Date(gatePass.createdAt).toLocaleString() || "N/A"],
          ["Mobile No", staff.basicDetails?.mobNo || "N/A"],
          [
            "Stoppage",
            (staff.transportFacility?.busStop || "N/A").toUpperCase(),
          ],
          [
            "Vehicle No",
            (staff.transportFacility?.vehicleNo || "N/A").toUpperCase(),
          ],
          [
            "Bus No",
            (staff.transportFacility?.busNumber || "N/A").toUpperCase(),
          ],
          ["Remark", "" || "N/A"],
        ],
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: { top: 1, right: 1, bottom: 1, left: 3 },
          overflow: "linebreak",
          textColor: 0,
          lineColor: ["#808080"],
        },
        columnStyles: { 0: { fontStyle: "bold" } },
        didDrawCell: function (data) {
          if (
            data.section === "body" &&
            (data.row.index === 0 ||
              data.row.index === data.table.body.length - 1 ||
              data.column.index === 0 ||
              data.column.index === data.table.columns.length - 1)
          ) {
            doc.setDrawColor(255, 255, 255);
            doc.rect(
              data.cell.x,
              data.cell.y,
              data.cell.width,
              data.cell.height,
              "S",
            );
          }
        },
      });

      const finalY = doc.lastAutoTable.finalY + 15;
      doc.setFont("helvetica", "bold").setFontSize(7);

      doc.text("Employee Signature", xOffset + 20, finalY, { align: "center" }); // ✅ Changed
      doc.text("Approved By", xOffset + 70, finalY, { align: "center" });

      window.open(doc.output("bloburl"), "_blank");
      return;
    }

    // ── STUDENT GATE PASS (unchanged) ────────────────────────────
    const admissionNumbers = Array.isArray(gatePass.admission_no)
      ? gatePass.admission_no
      : [gatePass.admission_no];

    for (let i = 0; i < admissionNumbers.length; i++) {
      const admissionNo = admissionNumbers[i];
      const student = await fetchStudentDetails(admissionNo);

      if (student) {
        if (passCount % 2 === 0 && passCount > 0) {
          rowStartY += 130;
          xOffset = 10;
        }

        if (rowStartY > 250) {
          doc.addPage();
          rowStartY = 25;
          xOffset = 10;
        }

        doc.setFillColor(240, 240, 240);
        doc.roundedRect(xOffset - 2, rowStartY - 2, 94, 65, 5, 5, "F");
        const schoolLogo = "/assets/images/icon.png";
        doc.addImage(schoolLogo, "PNG", xOffset, rowStartY, 13, 13);
        doc.setFont("helvetica", "bold").setFontSize(10.5);
        doc.text("Acharyakulam", xOffset + 16, rowStartY + 4);
        doc.setFont("helvetica", "normal").setFontSize(7);
        doc.text(
          "Tata Road, Namkum, Ranchi - 834010",
          xOffset + 16,
          rowStartY + 6.8,
        );
        doc.text(`Session ${session}`, xOffset + 16, rowStartY + 9);
        doc.setFont("helvetica", "bold").setFontSize(8);
        doc.setTextColor(239, 78, 36);
        doc.text("Student's Gate Pass", xOffset + 16, rowStartY + 12);
        doc.setTextColor(0, 0, 0);

        const tableStartX = xOffset;
        const tableStartY = rowStartY + 15;
        const tableWidth = 90;
        const tableHeight = 98;
        const cornerRadius = 5;
        const topPadding = 5;

        doc.setDrawColor(239, 78, 36);
        doc.setLineWidth(0.5);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(
          tableStartX,
          tableStartY,
          tableWidth,
          tableHeight,
          cornerRadius,
          cornerRadius,
          "DF",
        );

        autoTable(doc, {
          startY: tableStartY + topPadding,
          margin: { left: xOffset },
          tableWidth: tableWidth,
          body: [
            ["Gate Pass ID", gatePass.gatePassId || "N/A"],
            ["Name", student.name || "N/A"],
            ["Admission No.", student.admission_no || "N/A"],
            ["Roll No.", student.studentDetails?.rollNo || "N/A"],
            [
              "Class & Section",
              `${student.currentClass || "N/A"} - ${student.studentDetails?.currentSection || "N/A"}`,
            ],
            ["Purpose", gatePass.purpose || "N/A"],
            ["Remark", gatePass.remark || "N/A"],
            ["Out Time", gatePass.outTime || "N/A"],
            ["Date", new Date(gatePass.createdAt).toLocaleString() || "N/A"],
            ["Parent/Guardian", student.parentDetails?.[0]?.name || "N/A"],
            ["Mobile No", student.studentDetails?.phoneNo || "N/A"],
            [
              "Stoppage",
              (student.transportFacility?.busStop || "N/A").toUpperCase(),
            ],
            [
              "Vehicle No",
              (student.transportFacility?.vehicleNo || "N/A").toUpperCase(),
            ],
            [
              "Bus No",
              (student.transportFacility?.busNumber || "N/A").toUpperCase(),
            ],
            ["Remark", "" || "N/A"],
          ],
          theme: "grid",
          styles: {
            fontSize: 7,
            cellPadding: { top: 1, right: 1, bottom: 1, left: 3 },
            overflow: "linebreak",
            textColor: 0,
            lineColor: ["#808080"],
          },
          columnStyles: { 0: { fontStyle: "bold" } },
          didDrawCell: function (data) {
            if (
              data.section === "body" &&
              (data.row.index === 0 ||
                data.row.index === data.table.body.length - 1 ||
                data.column.index === 0 ||
                data.column.index === data.table.columns.length - 1)
            ) {
              doc.setDrawColor(255, 255, 255);
              doc.rect(
                data.cell.x,
                data.cell.y,
                data.cell.width,
                data.cell.height,
                "S",
              );
            }
          },
        });

        const finalY = doc.lastAutoTable.finalY + 15;
        doc.setFont("helvetica", "bold").setFontSize(7);
        doc.text("Parent/Guardian", xOffset + 20, finalY, { align: "center" });
        const sign = "/assets/images/sign.png";
        doc.addImage(sign, "PNG", xOffset + 60, finalY - 12, 20, 12);
        doc.text("Approved By", xOffset + 70, finalY, { align: "center" });
        xOffset += 100;
        passCount++;
      } else {
        console.error(`No data found for admission number: ${admissionNo}`);
      }
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const handleDelete = async (gatePassIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete Gate Pass ID "${gatePassIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_gatePass_mongodb`,
          { gatePassId: gatePassIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Gate Pass Deleted", { position: "top-right" });
          dispatch(fetchGatePassDetails());
        } else {
          toast.error("Please Try Again");
        }
      } catch (error) {
        toast.error("Please Try Again");
      }
    }
  };

  const handleExportGatePassExcel = () => {
    if (!gatePassDetails || gatePassDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = gatePassDetails.map((record) => ({
      "Gate Pass ID": record?.gatePassId || "",
      "Admission No": Array.isArray(record?.admission_no)
        ? record.admission_no.join(", ")
        : record?.admission_no || "",
      "Out Time": record?.outTime || "",
      Purpose: capitalizeWords(record?.purpose || ""),
      Remark: capitalizeWords(record?.remark || ""),
      Date: new Date(record?.createdAt).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      Status: capitalizeWords(record?.status || "Pending"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gate Pass");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `GatePass_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGatePassPendingExcel = () => {
    if (!gatePassDetails || gatePassDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const pendingRecords = gatePassDetails.filter(
      (record) => (record?.status || "").toLowerCase() === "pending",
    );

    if (pendingRecords.length === 0) {
      toast.warning("No pending gate pass records found");
      return;
    }

    const sheetData = pendingRecords.map((record) => ({
      "Gate Pass ID": record?.gatePassId || "",
      "Admission No": Array.isArray(record?.admission_no)
        ? record.admission_no.join(", ")
        : record?.admission_no || "",
      "Out Time": record?.outTime || "",
      Purpose: capitalizeWords(record?.purpose || ""),
      Remark: capitalizeWords(record?.remark || ""),
      Date: new Date(record?.createdAt).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      Status: capitalizeWords(record?.status || "Pending"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gate Pass");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `GatePass_Pending_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGatePassApprovedExcel = () => {
    if (!gatePassDetails || gatePassDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const pendingRecords = gatePassDetails.filter(
      (record) => (record?.status || "").toLowerCase() === "approved",
    );

    if (pendingRecords.length === 0) {
      toast.warning("No pending gate pass records found");
      return;
    }

    const sheetData = pendingRecords.map((record) => ({
      "Gate Pass ID": record?.gatePassId || "",
      "Admission No": Array.isArray(record?.admission_no)
        ? record.admission_no.join(", ")
        : record?.admission_no || "",
      "Out Time": record?.outTime || "",
      Purpose: capitalizeWords(record?.purpose || ""),
      Remark: capitalizeWords(record?.remark || ""),
      Date: new Date(record?.createdAt).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      Status: capitalizeWords(record?.status || "Approved"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gate Pass");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `GatePass_Approved_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div class="modal-header d-flex justify-content-between">
                    <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                      Create Gate Pass{" "}
                      <span className="ms-3 text-secondary">(05/08)</span>
                    </h5>
                    <div className="d-flex align-items-center">
                      <span class="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div class="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          class="form-check-input m-0"
                          checked
                        />
                      </div>
                      <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div class="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Type <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={[
                            { value: "student", label: "Student" },
                            { value: "staff", label: "Staff" },
                          ]}
                          value={
                            formData.type
                              ? {
                                  value: formData.type,
                                  label:
                                    formData.type === "student"
                                      ? "Student"
                                      : "Staff",
                                }
                              : null
                          }
                          onChange={(selected) => {
                            setFormData((prev) => ({
                              ...prev,
                              type: selected?.value,
                              admission_no: [],
                              staffId: "",
                            }));
                            setTempAdmission("");
                          }}
                          placeholder="Select Type"
                          classNamePrefix="react-select"
                        />
                      </div>

                      {formData.type === "student" ? (
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Admission No (Comma Separated){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control text-dark"
                            placeholder="Enter Admission No(s)"
                            rows="3"
                            value={tempAdmission}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTempAdmission(val);
                              if (val.includes(",")) {
                                const values = val
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter((v) => v !== "");
                                setFormData({
                                  ...formData,
                                  admission_no: values,
                                });
                              }
                            }}
                            onBlur={() => {
                              const values = tempAdmission
                                .split(",")
                                .map((v) => v.trim())
                                .filter((v) => v !== "");
                              setFormData({
                                ...formData,
                                admission_no: values,
                              });
                            }}
                          ></textarea>
                        </div>
                      ) : (
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Staff ID <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control text-dark"
                            placeholder="Enter Staff ID"
                            name="staffId"
                            value={formData.staffId}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}

                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Remark <span className="text-danger">*</span>
                        </label>
                        <textarea
                          type="text"
                          class="form-control text-dark"
                          placeholder="Enter Remark"
                          rows="3"
                          name="remark"
                          value={formData.remark}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>
                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Out Time <span className="text-danger">*</span>
                        </label>
                        <input
                          type="time"
                          class="form-control text-dark"
                          placeholder="..."
                          name="outTime"
                          value={formData.outTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Purpose / Reason{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          class="form-control text-dark"
                          placeholder="Enter Purpose / Reason"
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div class="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        class="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((gatePass, index) => (
                        <tr key={index}>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap">
                            <span class="text-dark">Gate Pass ID</span> &mdash;{" "}
                            {gatePass.gatePassId}
                            <br />
                            <span class="text-dark">Gate Pass Type</span>{" "}
                            &mdash;{" "}
                            {capitalizeWords(gatePass.type || "Student")}
                            <br />
                            {gatePass.type === "staff" ? (
                              <>
                                <span class="text-dark">Staff ID</span> &mdash;{" "}
                                {gatePass.staffId || "N/A"}
                              </>
                            ) : (
                              <>
                                <span class="text-dark">Admission No</span>{" "}
                                &mdash;{" "}
                                {Array.isArray(gatePass.admission_no)
                                  ? gatePass.admission_no.length > 2
                                    ? `${gatePass.admission_no.slice(0, 2).join(", ")} +${gatePass.admission_no.length - 2} More`
                                    : gatePass.admission_no.join(", ")
                                  : gatePass.admission_no || "N/A"}
                              </>
                            )}
                            <br />
                            <span class="text-dark">Out Time</span> &mdash;{" "}
                            {gatePass.outTime}
                            <br />
                            <span class="text-dark">Purpose</span> &mdash;{" "}
                            {gatePass.purpose
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase(),
                              )
                              .reduce((acc, word, index) => {
                                const last = acc.length - 1;
                                if (index % 5 === 0) {
                                  acc.push([word]);
                                } else {
                                  acc[last].push(word);
                                }
                                return acc;
                              }, [])
                              .map((group, i) => (
                                <span key={i}>
                                  {group.join(" ")}
                                  <br />
                                </span>
                              ))}
                            <span class="text-dark">Remark</span> &mdash;{" "}
                            {gatePass.remark
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase(),
                              )
                              .reduce((acc, word, index) => {
                                const last = acc.length - 1;
                                if (index % 5 === 0) {
                                  acc.push([word]);
                                } else {
                                  acc[last].push(word);
                                }
                                return acc;
                              }, [])
                              .map((group, i) => (
                                <span key={i}>
                                  {group.join(" ")}
                                  <br />
                                </span>
                              ))}
                            <span class="text-dark">Date</span> &mdash;{" "}
                            <span className="text-uppercase">
                              {new Date(gatePass.createdAt).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}
                            </span>
                            <br />
                            <div className="mt-2">
                              <span class="badge-dot bg-success me-2 d-inline-block align-middle text-capitalize"></span>
                              {capitalizeWords(gatePass.status)}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span
                                onClick={() => handleDownloadPDF(gatePass)}
                                class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Download
                              </span>
                              <span
                                onClick={() =>
                                  handleDelete(gatePass.gatePassId)
                                }
                                class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        data-bs-toggle="modal"
                        data-bs-target=".gd-example-modal-lg"
                        className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Create Gate Pass
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Front Office</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Gate Pass
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {fetchLoading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Gate Pass
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.4 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportGatePassExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Gate Pass Report
                              </span>
                              <span
                                onClick={handleExportGatePassPendingExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Pending
                              </span>
                              <span
                                onClick={handleExportGatePassApprovedExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Approved
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Student's Gate Pass Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default GatePass;
